import { create } from 'zustand';
import type { AdminSubcategory } from '@/domain/model/AdminSubcategory';
import type { CreateSubcategoryRequest } from '@/application/dto/CreateSubcategoryRequest';
import type { UpdateSubcategoryRequest } from '@/application/dto/UpdateSubcategoryRequest';
import { adminService } from '@/infrastructure/store/adminServiceInstance';
import { resolveErrorKey } from '@/infrastructure/util/error';

interface AdminSubcategoryState {
  subcategories: AdminSubcategory[];
  isLoading: boolean;
  error: string | null;
}

interface AdminSubcategoryActions {
  fetchSubcategories: (categoryId?: string) => Promise<void>;
  createSubcategory: (data: CreateSubcategoryRequest) => Promise<boolean>;
  updateSubcategory: (id: string, data: UpdateSubcategoryRequest) => Promise<boolean>;
  deleteSubcategory: (id: string) => Promise<boolean>;
  clearError: () => void;
}

export const useAdminSubcategoryStore = create<AdminSubcategoryState & AdminSubcategoryActions>(
  (set) => ({
    subcategories: [],
    isLoading: false,
    error: null,

    fetchSubcategories: async (categoryId?: string) => {
      set({ isLoading: true, error: null });
      try {
        const subcategories = await adminService.getSubcategories(categoryId);
        set({ subcategories, isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.subcategoryLoadFailed') });
      }
    },

    createSubcategory: async (data: CreateSubcategoryRequest) => {
      set({ isLoading: true, error: null });
      try {
        await adminService.createSubcategory(data);
        set({ isLoading: false });
        return true;
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.subcategorySaveFailed') });
        return false;
      }
    },

    updateSubcategory: async (id: string, data: UpdateSubcategoryRequest) => {
      set({ isLoading: true, error: null });
      try {
        await adminService.updateSubcategory(id, data);
        set({ isLoading: false });
        return true;
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.subcategorySaveFailed') });
        return false;
      }
    },

    deleteSubcategory: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        await adminService.deleteSubcategory(id);
        set({ isLoading: false });
        return true;
      } catch {
        set({ isLoading: false });
        return false;
      }
    },

    clearError: () => set({ error: null }),
  }),
);
